/**
 * SeveritySelector Component
 *
 * Visual severity selector with colored buttons for incident reports.
 * Each severity level has its own color, icon, and description.
 *
 * @module pages/technician/incidents/SeveritySelector
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  FiInfo,
  FiAlertCircle,
  FiAlertTriangle,
  FiAlertOctagon,
} from 'react-icons/fi';
import { INCIDENT_SEVERITY } from '../../../utils/constants';
import './IncidentReportForm.css';

const SEVERITY_CONFIG = {
  [INCIDENT_SEVERITY.LOW]: {
    icon: FiInfo,
    label: 'Baja',
    description: 'No afecta el trabajo',
    color: '#28a745',
    bgColor: 'rgba(40, 167, 69, 0.1)',
    borderColor: 'rgba(40, 167, 69, 0.3)',
  },
  [INCIDENT_SEVERITY.MEDIUM]: {
    icon: FiAlertCircle,
    label: 'Media',
    description: 'Afecta parcialmente',
    color: '#ffc107',
    bgColor: 'rgba(255, 193, 7, 0.1)',
    borderColor: 'rgba(255, 193, 7, 0.3)',
  },
  [INCIDENT_SEVERITY.HIGH]: {
    icon: FiAlertTriangle,
    label: 'Alta',
    description: 'Bloquea trabajo',
    color: '#fd7e14',
    bgColor: 'rgba(253, 126, 20, 0.1)',
    borderColor: 'rgba(253, 126, 20, 0.3)',
  },
  [INCIDENT_SEVERITY.CRITICAL]: {
    icon: FiAlertOctagon,
    label: 'Critica',
    description: 'Emergencia/Seguridad',
    color: '#dc3545',
    bgColor: 'rgba(220, 53, 69, 0.1)',
    borderColor: 'rgba(220, 53, 69, 0.3)',
  },
};

const SeveritySelector = ({ value, onChange, disabled }) => {
  const severityLevels = [
    INCIDENT_SEVERITY.LOW,
    INCIDENT_SEVERITY.MEDIUM,
    INCIDENT_SEVERITY.HIGH,
    INCIDENT_SEVERITY.CRITICAL,
  ];

  return (
    <div className="severity-selector">
      <div className="severity-options">
        {severityLevels.map((severity) => {
          const config = SEVERITY_CONFIG[severity];
          const Icon = config.icon;
          const isSelected = value === severity;

          return (
            <button
              key={severity}
              type="button"
              className={`severity-option ${isSelected ? 'selected' : ''}`}
              onClick={() => !disabled && onChange(severity)}
              disabled={disabled}
              style={{
                '--severity-color': config.color,
                '--severity-bg': isSelected ? config.bgColor : 'transparent',
                '--severity-border': isSelected ? config.color : config.borderColor,
              }}
              aria-pressed={isSelected}
              aria-label={`Severidad ${config.label}: ${config.description}`}
            >
              <div className="severity-icon">
                <Icon size={24} />
              </div>
              <div className="severity-info">
                <span className="severity-label">{config.label}</span>
                <span className="severity-description">{config.description}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

SeveritySelector.propTypes = {
  value: PropTypes.oneOf(Object.values(INCIDENT_SEVERITY)),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SeveritySelector.defaultProps = {
  value: null,
  disabled: false,
};

export default SeveritySelector;
